import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
export function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy - School Launch';
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-black text-white font-sans selection:bg-amber-500 selection:text-black" data-id="element-185">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-zinc-800" data-id="element-186">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between" data-id="element-187">
          <Link to="/" className="flex items-center" aria-label="SchoolLaunch - Home" data-id="element-188">
            <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-10 w-auto" data-id="element-189" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-400 hover:text-amber-500 transition-colors" data-id="element-190">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-191" />
            <span className="hidden sm:inline" data-id="element-192">Back to Home</span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8" data-id="element-193">
        <div className="max-w-4xl mx-auto text-center" data-id="element-194">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-amber-500/10 text-amber-500 font-medium text-xs md:text-sm mb-6 border border-amber-500/20" data-id="element-195">
            Legal
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight" data-id="element-196">
            Privacy{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600" data-id="element-197">
              Policy
            </span>
          </h1>
          <p className="text-zinc-400 text-sm md:text-base" data-id="element-198">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8" data-id="element-199">
        <div className="max-w-3xl mx-auto" data-id="element-200">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6 md:p-10 backdrop-blur space-y-10" data-id="element-201">
            <div data-id="element-202">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-203">
                1. Introduction
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-204">
                Welcome to{' '}
                <span className="font-bold text-white" data-id="element-205">School Launch</span>! We
                are committed to protecting your personal information and your
                right to privacy. If you have any questions or concerns about
                this privacy notice or our practices regarding your personal
                information, please contact us at{' '}
                <a href="mailto:contact@school-launch.com" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-206">
                  contact@school-launch.com
                </a>
                .
              </p>
              <p className="text-zinc-300 leading-relaxed mt-4" data-id="element-207">
                When you visit our website at{' '}
                <a href="https://school-launch.com/" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-208">
                  https://school-launch.com/
                </a>{' '}
                and use any of our services, we appreciate that you trust us
                with your personal information. We take your privacy very
                seriously. In this privacy notice we seek to explain as clearly
                as possible what information we collect, how we use it, and what
                rights you have in relation to it. Please read this privacy
                policy carefully.
              </p>
            </div>

            <div data-id="element-209">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-210">
                2. What Information Do We Collect?
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-4" data-id="element-211">
                We collect personal information that you voluntarily provide to
                us when you submit a form on our website, express an interest in
                obtaining information about schools or educational programs, or
                communicate with us.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-3" data-id="element-212">
                The personal information we collect may include:
              </p>
              <ul className="space-y-2 text-zinc-300" data-id="element-213">
                {['Full name', 'Email address', 'Phone number', 'Location (city, state)', 'Educational interests and preferences', 'Other information you voluntarily provide'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-214">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-215" />
                    <span data-id="element-216">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-217">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-218">
                3. How Do We Use Your Information?
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-3" data-id="element-219">
                We use the personal information collected through our website
                for the following purposes:
              </p>
              <ul className="space-y-2 text-zinc-300" data-id="element-220">
                {['To connect you with educational institutions that match your interests', 'To send you relevant information about school programs and enrollment opportunities', 'To send administrative and service-related communications', 'To respond to your inquiries and provide support', 'To send promotional and marketing communications (with your consent)', 'To improve our website and services', 'To comply with legal obligations'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-221">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-222" />
                    <span data-id="element-223">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-224">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-225">
                4. SMS Communications
              </h2>
              <div className="space-y-4 text-zinc-300 leading-relaxed" data-id="element-226">
                <p data-id="element-227">
                  We collect mobile phone numbers when users voluntarily submit
                  their information through our website forms and explicitly
                  consent to receive SMS communications from School Launch.
                </p>
                <p data-id="element-228">
                  SMS messages may include enrollment information, school
                  program details, follow-up messages, appointment
                  confirmations, and education-related notifications.
                </p>
                <p data-id="element-229">
                  Message frequency may vary. Message and data rates may apply.
                </p>
                <p data-id="element-230">
                  Users may opt out of SMS communications at any time by
                  replying{' '}
                  <span className="text-amber-500 font-bold" data-id="element-231">STOP</span> to any
                  message, or request assistance by replying{' '}
                  <span className="text-amber-500 font-bold" data-id="element-232">HELP</span>.
                </p>
                <p data-id="element-233">
                  We do not sell or share mobile phone numbers collected for SMS
                  communications with third parties for marketing purposes.
                </p>
                <p data-id="element-234">
                  All SMS opt-in data and consent information will not be shared
                  with any third parties.
                </p>
              </div>
            </div>

            <div data-id="element-235">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-236">
                5. Will Your Information Be Shared With Anyone?
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-4" data-id="element-237">
                We may process or share data we hold based on the following
                legal bases:
              </p>
              <ul className="space-y-3 text-zinc-300 mb-4" data-id="element-238">
                {[{
                label: 'Consent:',
                text: 'We may process your data if you have given us specific consent to use your personal information for a specific purpose.'
              }, {
                label: 'Legitimate Interests:',
                text: 'We may process your data where reasonably necessary to pursue our legitimate business interests.'
              }, {
                label: 'Performance of a Contract:',
                text: 'Where we have entered into a contract with you, we may process your personal information to fulfill the terms of our contract.'
              }, {
                label: 'Legal Obligations:',
                text: 'We may disclose your information where legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process.'
              }].map(item => <li key={item.label} className="flex items-start gap-3" data-id="element-239">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-240" />
                    <span data-id="element-241">
                      <span className="font-bold text-white" data-id="element-242">{item.label}</span>{' '}
                      {item.text}
                    </span>
                  </li>)}
              </ul>
              <p className="text-zinc-300 leading-relaxed mb-4" data-id="element-243">
                We may share your information with educational institutions and
                school partners for the purpose of fulfilling your enrollment
                inquiry. We do not sell your personal information to third
                parties.
              </p>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-id="element-244">
                <p className="text-zinc-200 leading-relaxed font-medium" data-id="element-245">
                  No mobile information will be shared with third parties or
                  affiliates for marketing or promotional purposes. All other
                  use case categories exclude text messaging originator opt-in
                  data and consent; this information will not be shared with any
                  third parties.
                </p>
              </div>
            </div>

            <div data-id="element-246">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-247">
                6. Cookies and Similar Technologies
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-4" data-id="element-248">
                We use cookies and similar tracking technologies on our website
                to personalize your experience and improve our services. Cookies
                are small files placed on your device that enable various
                features and functionality.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-3 font-bold text-white" data-id="element-249">
                Types of cookies we use:
              </p>
              <ul className="space-y-3 text-zinc-300 mb-4" data-id="element-250">
                {[{
                label: 'Essential Cookies:',
                text: 'Necessary for the website to function properly. These cannot be disabled.'
              }, {
                label: 'Analytics and Performance Cookies:',
                text: 'Used to collect aggregated information about website traffic and usage to help us improve the site.'
              }, {
                label: 'Functionality Cookies:',
                text: 'Remember your preferences and settings to provide a personalized experience.'
              }, {
                label: 'Targeting and Advertising Cookies:',
                text: 'Used to deliver relevant advertising based on your browsing habits and to measure the effectiveness of our ad campaigns (including Meta/Facebook ads).'
              }].map(item => <li key={item.label} className="flex items-start gap-3" data-id="element-251">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-252" />
                    <span data-id="element-253">
                      <span className="font-bold text-white" data-id="element-254">{item.label}</span>{' '}
                      {item.text}
                    </span>
                  </li>)}
              </ul>
              <p className="text-zinc-300 leading-relaxed" data-id="element-255">
                <span className="font-bold text-white" data-id="element-256">
                  Your choices regarding cookies:
                </span>{' '}
                You can disable cookies through your browser settings. Note that
                disabling cookies may affect the functionality of this and other
                websites. For more information, visit{' '}
                <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-257">
                  www.allaboutcookies.org
                </a>
                .
              </p>
            </div>

            <div data-id="element-258">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-259">
                7. How Long Do We Keep Your Information?
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-260">
                We will only keep your personal information for as long as it is
                necessary for the purposes set out in this privacy policy,
                unless a longer retention period is required or permitted by
                law.
              </p>
            </div>

            <div data-id="element-261">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-262">
                8. How Do We Keep Your Information Safe?
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-263">
                We implement organizational and technical security measures
                designed to protect your personal information from accidental
                loss and from unauthorized access, use, or disclosure. However,
                no system is completely secure and we cannot guarantee absolute
                security.
              </p>
            </div>

            <div data-id="element-264">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-265">
                9. Minors
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-266">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you believe we have inadvertently
                collected information from a minor, please contact us
                immediately at{' '}
                <a href="mailto:contact@school-launch.com" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-267">
                  contact@school-launch.com
                </a>
                .
              </p>
            </div>

            <div data-id="element-268">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-269">
                10. Your Privacy Rights
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-270">
                Depending on your location, you may have certain rights
                regarding your personal information, including the right to
                access, correct, or delete the data we hold about you. To
                exercise these rights, please contact us at{' '}
                <a href="mailto:contact@school-launch.com" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-271">
                  contact@school-launch.com
                </a>
                .
              </p>
            </div>

            <div data-id="element-272">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-273">
                11. Do-Not-Track Features
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-274">
                Our website does not currently alter its behavior in response to
                Do-Not-Track (DNT) signals from browsers. We track user activity
                using web server logs, cookies, and similar technologies as
                described in this policy.
              </p>
            </div>

            <div data-id="element-275">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-276">
                12. Updates to This Policy
              </h2>
              <p className="text-zinc-300 leading-relaxed" data-id="element-277">
                We may update this privacy policy from time to time. The updated
                version will be effective as soon as it is accessible. We
                encourage you to review this policy periodically.
              </p>
            </div>

            <div data-id="element-278">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" data-id="element-279">
                13. Contact Us
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-4" data-id="element-280">
                If you have questions or comments about this policy, please
                contact us at:
              </p>
              <div className="rounded-xl border border-zinc-800 bg-black/40 p-5 space-y-2" data-id="element-281">
                <p className="text-zinc-300" data-id="element-282">
                  <span className="font-bold text-white" data-id="element-283">Email:</span>{' '}
                  <a href="mailto:contact@school-launch.com" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-284">
                    contact@school-launch.com
                  </a>
                </p>
                <p className="text-zinc-300" data-id="element-285">
                  <span className="font-bold text-white" data-id="element-286">Phone:</span>{' '}
                  <a href="tel:9044672448" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-287">
                    904-467-2448
                  </a>
                </p>
                <p className="text-zinc-300" data-id="element-288">
                  <span className="font-bold text-white" data-id="element-289">Website:</span>{' '}
                  <a href="https://school-launch.com/" className="text-amber-500 hover:text-amber-400 transition-colors" data-id="element-290">
                    https://school-launch.com/
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 border-t border-zinc-900" data-id="element-291">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-292">
          <Link to="/" className="flex items-center" aria-label="SchoolLaunch - Home" data-id="element-293">
            <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-8 w-auto" data-id="element-294" />
          </Link>
          <div className="text-zinc-500 text-sm text-center" data-id="element-295">
            &copy; {new Date().getFullYear()} Engage Media Group. All rights
            reserved.
          </div>
          <div className="flex gap-6 text-sm text-zinc-400" data-id="element-296">
            <Link to="/privacy-policy" className="hover:text-amber-500 transition-colors" data-id="element-297">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-amber-500 transition-colors" data-id="element-298">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>;
}